import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

interface Table {
  id: string;
  number: string;
  name: string | null;
  status: string;
}

interface TabletTableSelectorProps {
  tables: Table[];
  selectedTable: string;
  onSelect: (tableNumber: string) => void;
}

export function TabletTableSelector({
  tables,
  selectedTable,
  onSelect,
}: TabletTableSelectorProps) {
  const [manualInput, setManualInput] = useState("");

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualInput.trim()) {
      onSelect(manualInput.trim());
    }
  };

  // Sort tables by number
  const sortedTables = [...tables].sort((a, b) => {
    const numA = parseInt(a.number) || 0;
    const numB = parseInt(b.number) || 0;
    return numA - numB;
  });

  return (
    <div className="space-y-6">
      {/* Table grid with scroll */}
      <div className="max-h-[50vh] overflow-y-auto pr-2">
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
          {sortedTables.map((table) => {
            const isSelected = selectedTable === table.number;
            const isOccupied = table.status === "occupied";

            return (
              <button
                key={table.id}
                onClick={() => onSelect(table.number)}
                className={cn(
                  "relative aspect-square rounded-xl flex flex-col items-center justify-center transition-all duration-200",
                  "border-2 text-lg font-bold",
                  isSelected
                    ? "bg-primary border-primary text-primary-foreground scale-105 shadow-lg shadow-primary/30"
                    : isOccupied
                      ? "bg-amber-500/20 border-amber-500/50 text-amber-400 hover:border-amber-400"
                      : "bg-slate-700/50 border-slate-600 text-slate-300 hover:border-primary/50 hover:bg-slate-700"
                )}
              >
                {isSelected && (
                  <Check className="absolute top-1 right-1 w-4 h-4" />
                )}
                <span className="text-2xl">{table.number}</span>
                {table.name && (
                  <span className="text-xs opacity-70 truncate w-full px-1 text-center">
                    {table.name}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Manual input */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-400">Ou digite o número:</span>
        <form onSubmit={handleManualSubmit} className="flex gap-2 flex-1 max-w-xs">
          <Input
            type="text"
            placeholder="Ex: 15"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            className="bg-slate-900/50 border-slate-600 text-white"
          />
          <Button type="submit" variant="secondary" disabled={!manualInput.trim()}>
            OK
          </Button>
        </form>
      </div>

      {/* Selected table info */}
      {selectedTable && (
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 text-center">
          <p className="text-primary text-lg font-medium">
            Mesa selecionada: <span className="font-bold">{selectedTable}</span>
          </p>
        </div>
      )}
    </div>
  );
}
